import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import type Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Minus, Plus, ArrowRight, ChevronLeft, ChevronRight, Crown, Medal, Clock, Quote, Target, Eye, Gem, Check, ShieldCheck, Headset, Star } from "lucide-react";
import { WhatsAppIcon, GoogleIcon } from "@/components/toraflex/BrandIcons";
import { useGsapAnimations } from "@/hooks/useGsapAnimations";
import Navbar from "@/components/toraflex/Navbar";
import Footer from "@/components/toraflex/Footer";
import VimeoVideoCard from "@/components/toraflex/VimeoVideoCard";
import FloatingButtons from "@/components/toraflex/FloatingButtons";
import WhatsAppContactModal from "@/components/toraflex/whatsapp/WhatsAppContactModal";
import SidebarForm, { LeadForm } from "@/components/toraflex/SidebarForm";
import avatar1 from "@/assets/avatar-1.webp";
import avatar2 from "@/assets/avatar-2.webp";
import avatar3 from "@/assets/avatar-3.webp";
import bgForm from "@/assets/bg-form-new.webp";
import logoComfort from "@/assets/logos/comfort.webp";
import logoTopColchoes from "@/assets/logos/top-colchoes.webp";
import logoTemTudo from "@/assets/logos/tem-tudo.webp";
import logoLbxMoveis from "@/assets/logos/lbx-moveis.webp";
import logoMobigu from "@/assets/logos/mobigu.webp";
import logoSonhare from "@/assets/logos/sonhare.webp";
import logoAguiarFlex from "@/assets/logos/aguiar-flex.webp";
import logoCiaSouza from "@/assets/logos/cia-souza.webp";
import logoColchoesCastro from "@/assets/logos/colchoes-castro.webp";
import logoPauEFilhos from "@/assets/logos/pau-e-filhos.webp";
import logoLvMoveis from "@/assets/logos/lv-moveis.webp";
import logoMgColchoes from "@/assets/logos/mg-colchoes.webp";
import logoMaiaMoveis from "@/assets/logos/maia-moveis.webp";
import logoColchoariaClick from "@/assets/logos/colchoaria-click.webp";
import logoMoveisOutlet from "@/assets/logos/moveis-outlet.webp";
import logoShoppingMoveis from "@/assets/logos/shopping-dos-moveis.webp";
import { useDragScroll } from "@/hooks/useDragScroll";
import qualidadeImg from "@/assets/qualidade.webp";
import fabricaImg from "@/assets/fabrica-toraflex.webp";
import logoInmetro from "@/assets/logo-inmetro.svg";


/* ─── FAQ Item ─────────────────────────────────── */
function FaqItem({ question, answer, className, open, onToggle }: { question: string; answer: string; className?: string; open: boolean; onToggle: () => void }) {
  return (
    <div
      className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-all duration-300 cursor-pointer ${open ? "border-brand-blue/30 shadow-md" : "border-slate-100"} ${className || ""}`}
      onClick={onToggle}
    >
      <div className="py-3.5 px-5 flex justify-between items-center group">
        <h3 className={`font-heading font-bold text-[14px] pr-4 transition-colors ${open ? "text-brand-red" : "text-brand-blue group-hover:text-brand-red"}`}>{question}</h3>
        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 border transition-colors ${open ? "bg-brand-red/10 border-brand-red/20" : "bg-slate-50 border-slate-100"}`}>
          {open ? <Minus className="w-3 h-3 text-brand-red" /> : <Plus className="w-3 h-3 text-brand-red" />}
        </div>
      </div>
      <div
        className={`overflow-hidden transition-all duration-300 ${open ? "max-h-[200px] opacity-100" : "max-h-0 opacity-0"}`}
      >
        <p className="px-5 pb-4 text-slate-500 text-[13px] leading-relaxed border-t border-slate-50 pt-3">{answer}</p>
      </div>
    </div>
  );
}

/* ─── FAQ List (accordion) ──────────────────────── */
function FaqList({ faqs }: { faqs: { question: string; answer: string }[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return (
    <div className="space-y-2">
      {faqs.map((f, i) => (
        <FaqItem
          key={f.question}
          {...f}
          className="gsap-faq-item"
          open={openIndex === i}
          onToggle={() => setOpenIndex(openIndex === i ? null : i)}
        />
      ))}
    </div>
  );
}


/* ─── Hero Slides ────────────────────────────────── */
const heroSlides = [
  {
    imgDesktop: "/banner-1.webp",
    imgMobile: "/banner-1-mobile.webp",
    srcSet: "/banner-1.webp 1920w",
    srcSetMobile: "/banner-1-mobile.webp 750w",
    imgPosition: "object-[68%_center]",
    badge: "Qualidade & Durabilidade",
    title: (<>A escolha certa para todos os <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-white">estilos de sono.</span></>),
    desc: "Linha completa em espuma, molas e ortopédicos.",
    ctaLabel: "Conheça nossos produtos",
    ctaHref: "/produtos",
  },
  {
    imgDesktop: "/banner-2.webp",
    imgMobile: "/banner-2-mobile.webp",
    srcSet: "/banner-2.webp 1920w",
    srcSetMobile: "/banner-2-mobile.webp 750w",
    imgPosition: "object-center",
    badge: "+40 Anos de História",
    title: (<>Há mais de 40 anos levando <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-white">qualidade</span> até você.</>),
    desc: "Estrutura sólida. Produção em grande escala. Confiança comprovada.",
    ctaLabel: "Conheça a Toraflex",
    ctaHref: "/historia",
  },
];

/* ─── Main Page ─────────────────────────────────── */
export default function Index() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [waModalOpen, setWaModalOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setActiveSlide((s) => (s + 1) % heroSlides.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [paused]);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 400);
    const onResize = () => { cardWidthCache.current = 0; };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // GSAP Animations
  useGsapAnimations();

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) return; // Native scroll on mobile — no Lenis

    let lenisInstance: Lenis | null = null;

    import("lenis").then(({ default: LenisClass }) => {
      lenisInstance = new LenisClass({
        duration: 1.2,
        easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
      });

      // ─ Connect Lenis to GSAP ScrollTrigger so pinned sections stay in sync
      lenisInstance.on("scroll", ScrollTrigger.update);
      gsap.ticker.add((time) => {
        lenisInstance?.raf(time * 1000);
      });
      gsap.ticker.lagSmoothing(0);
    });

    return () => {
      lenisInstance?.destroy();
    };
  }, []);

  const products = [
    {
      img: "/espuma.webp",
      badge: "Mais Vendido",
      title: "Colchões de Espuma",
      filter: "espuma",
      desc: "Alta densidade e conforto adaptável. Suporte ideal para o corpo e noites revigorantes.",
    },
    {
      img: "/MOLA.webp",
      badge: "Campeão de Venda",
      title: "Colchões de Mola",
      filter: "molas",
      desc: "Equilibrio perfeito entre suporte e conforto sem transferência de movimentos, devido graças ao excelente conjunto de molas ensacadas",
    },
    {
      img: "/ORTOPEDICO.webp",
      badge: "Clássico",
      title: "Colchões Ortopédicos",
      filter: "ortopedicos",
      desc: "Máxima durabilidade, ideal para quem aprecia/precisa de um colchão com o conforto extra firme",
      imgZoom: true,
    },
    {
      img: "/base-box.webp",
      badge: "Novidade",
      title: "Base Box",
      filter: "box",
      desc: "Bases elegantes e resistentes. Estrutura reforçada de madeira para prolongar a vida útil do seu colchão.",
    },
  ];

  const vimeoVideos = [
    { vimeoId: "1166644831", fallbackTitle: "Suntuoso" },
    { vimeoId: "1166644660", fallbackTitle: "Acalento" },
    { vimeoId: "1166644681", fallbackTitle: "Cantata" },
    { vimeoId: "1166644758", fallbackTitle: "Osquestra" },
    { vimeoId: "1166644632", fallbackTitle: "Carrara" },
    { vimeoId: "1166644607", fallbackTitle: "Eco Soft D33" },
    { vimeoId: "1166644586", fallbackTitle: "Classic Plus" },
    { vimeoId: "1166644905", fallbackTitle: "Eco Visco D33" },
    { vimeoId: "1166644863", fallbackTitle: "Serenata" },
    { vimeoId: "1166644710", fallbackTitle: "Top Quality D45" },
    { vimeoId: "1166644728", fallbackTitle: "Top Quality D35" },
    { vimeoId: "1166644774", fallbackTitle: "Premier" },
    { vimeoId: "1166644805", fallbackTitle: "Quality Plus" },
    { vimeoId: "1166644880", fallbackTitle: "Marfim Black" },
  ];

  // Carousel state
  const videoCarouselRef = useRef<HTMLDivElement>(null);
  const [activeVideoPage, setActiveVideoPage] = useState(0);

  // Drag scroll for video carousel
  const videoDrag = useDragScroll<HTMLDivElement>();

  // Merge refs for video carousel
  const setVideoRef = useCallback((el: HTMLDivElement | null) => {
    (videoCarouselRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    (videoDrag.ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
  }, []);

  // Drag scroll for reviews marquee
  const reviewsDrag = useDragScroll<HTMLDivElement>();

  // Drag scroll for partner logos marquee
  const logosDrag = useDragScroll<HTMLDivElement>();

  const visibleCount = typeof window !== "undefined" && window.innerWidth >= 768 ? 4 : 1;
  const totalPages = Math.ceil(vimeoVideos.length / visibleCount);
  const cardWidthCache = useRef(0);

  const getCardWidth = useCallback(() => {
    if (cardWidthCache.current) return cardWidthCache.current;
    const container = videoCarouselRef.current;
    if (!container) return 0;
    const card = container.querySelector(".video-card") as HTMLElement;
    if (!card) return 0;
    cardWidthCache.current = card.offsetWidth + 24; // 24 = gap-6
    return cardWidthCache.current;
  }, []);

  const scrollToVideoPage = (page: number) => {
    const container = videoCarouselRef.current;
    if (!container) return;
    const cardWidth = getCardWidth();
    if (!cardWidth) return;
    const scrollAmount = page * visibleCount * cardWidth;
    container.scrollTo({ left: scrollAmount, behavior: "smooth" });
    setActiveVideoPage(page);
  };

  const handleVideoScroll = () => {
    const container = videoCarouselRef.current;
    if (!container) return;
    const cardWidth = getCardWidth();
    if (!cardWidth) return;
    const currentIndex = Math.round(container.scrollLeft / cardWidth);
    setActiveVideoPage(Math.floor(currentIndex / visibleCount));
  };


  const faqs = [
    {
      question: "Qual é o volume mínimo para o primeiro pedido?",
      answer: "O volume mínimo para o primeiro pedido é de 10 peças, podendo variar conforme a região de atendimento.",
    },
    {
      question: "Vocês entregam na minha região?",
      answer:
        "Nossa operação é focada no atendimento à região Sudeste do Brasil. Para consultar se atendemos sua cidade, entre em contato pelos nossos canais de atendimento.",
    },
    {
      question: "Como funciona a garantia e assistência técnica?",
      answer:
        "Oferecemos garantia de fábrica em toda a linha e um processo de assistência técnica rápido, sem burocracia, para que sua loja e seu cliente nunca fiquem na mão.",
    },
  ];

  return (
    <div className="font-sans text-slate-600 bg-[#F8FAFC] antialiased selection:bg-brand-red selection:text-white overflow-x-hidden">
      <Navbar onOpenSidebar={() => setSidebarOpen(true)} />
      <SidebarForm isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main>

      {/* ─── HERO CAROUSEL ───────────────────────────────────── */}
      <section
        className="relative gsap-stack overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Slides — crossfade */}
        {heroSlides.map((slide, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-1000 ${activeSlide === i ? "opacity-100 z-[1]" : "opacity-0 z-0"}`}
            role="presentation"
          >
            {/* Background — mobile e desktop */}
            <div className="absolute inset-0">
              {/* Mobile */}
              <img
                src={slide.imgMobile}
                srcSet={slide.srcSetMobile}
                sizes="100vw"
                alt=""
                className={`absolute inset-0 w-full h-full object-cover md:hidden ${slide.imgPosition}`}
                width={750}
                height={1200}
                fetchPriority={i === 0 ? "high" : "auto"}
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
              />
              {/* Desktop */}
              <img
                src={slide.imgDesktop}
                srcSet={slide.srcSet}
                sizes="100vw"
                alt=""
                className={`absolute inset-0 w-full h-full object-cover hidden md:block ${slide.imgPosition}`}
                width={1920}
                height={1080}
                fetchPriority={i === 0 ? "high" : "auto"}
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
              />
              {/* Gradientes */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-slate-900/30 md:hidden" />
              <div className="absolute inset-0 hidden md:block lg:hidden bg-gradient-to-r from-slate-900/90 via-slate-900/50 to-transparent" />
              <div className="absolute inset-0 hidden lg:block bg-gradient-to-r from-slate-900/95 via-slate-900/60 via-[45%] to-transparent to-[65%]" />
            </div>
          </div>
        ))}

        {/* Content */}
        <div className="relative z-10 bg-transparent min-h-[100svh] flex items-end pb-20 md:items-center md:min-h-[115vh] md:pt-20 md:pb-32 w-full pointer-events-none">
          <div className="w-full max-w-[1300px] mx-auto px-6 pointer-events-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center py-10 md:py-0 md:min-h-[calc(100vh-5rem)]">

              <div id="hero-content" className="max-w-xl flex flex-col justify-center md:-mt-20">
                {/* Badge */}
                {heroSlides[activeSlide].badge && (
                <div
                  key={activeSlide + "-badge"}
                  className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-brand-blue/30 border border-brand-blue/50 text-blue-100 text-[11px] font-bold tracking-wide uppercase mb-4 shadow-sm backdrop-blur-sm w-fit animate-fadeInUp"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                  {heroSlides[activeSlide].badge}
                </div>
                )}

                {/* Title */}
                <h1
                  key={activeSlide + "-title"}
                  className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1] mb-4 tracking-tight animate-fadeInUp text-balance"
                >
                  {heroSlides[activeSlide].title}
                </h1>

                {/* Desc */}
                <p
                  key={activeSlide + "-desc"}
                  className="text-lg md:text-xl text-slate-300 mb-6 leading-relaxed font-medium max-w-md animate-fadeInUp"
                >
                  {heroSlides[activeSlide].desc}
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  {"ctaHref" in heroSlides[activeSlide] ? (
                    <Link
                      to={(heroSlides[activeSlide] as { ctaHref: string }).ctaHref}
                      className="group relative inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-redHover text-white text-sm sm:text-base font-bold px-4 sm:px-6 py-3 rounded-xl shadow-lg hover:shadow-red-500/30 transition-all duration-300 transform hover:-translate-y-1 whitespace-nowrap w-full sm:w-auto uppercase tracking-widest"
                    >
                      {(heroSlides[activeSlide] as { ctaLabel: string }).ctaLabel}
                      <ArrowRight className="w-3 h-3 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setSidebarOpen(true)}
                      className="group relative inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-redHover text-white text-sm sm:text-base font-bold px-4 sm:px-6 py-3 rounded-xl shadow-lg hover:shadow-red-500/30 transition-all duration-300 transform hover:-translate-y-1 whitespace-nowrap w-full sm:w-auto"
                    >
                      <WhatsAppIcon className="w-5 h-5 flex-shrink-0" />
                      <span>Falar com Representante</span>
                      <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300 hidden sm:inline" />
                    </button>
                  )}
                </div>

                <div className="mt-6 flex items-center gap-3 text-xs text-slate-300 font-medium">
                  <div className="flex -space-x-2">
                    {[avatar1, avatar2, avatar3].map((src, i) => (
                      <img
                        key={i}
                        className="w-8 h-8 rounded-full border-2 border-slate-900 object-cover"
                        src={src}
                        alt="Cliente parceiro"
                        width={32}
                        height={32}
                        loading="lazy"
                        decoding="async"
                      />
                    ))}
                  </div>
                  <p>Junte-se a +2.000 clientes parceiros</p>
                </div>

                {/* Controls — setas + dots */}
                <div className="mt-8 flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => setActiveSlide((s) => (s - 1 + heroSlides.length) % heroSlides.length)}
                    className="w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-brand-red hover:border-brand-red transition-all duration-300 backdrop-blur-sm"
                    aria-label="Banner anterior"
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </button>

                  <div className="flex items-center gap-3">
                    {heroSlides.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setActiveSlide(i)}
                        aria-label={`Banner ${i + 1}`}
                        className={`relative h-11 flex items-center justify-center ${activeSlide === i ? "w-8" : "w-4"}`}
                      >
                        <span className={`block h-[3px] w-full rounded-full transition-all duration-500 ${activeSlide === i ? "bg-white" : "bg-white/40"}`} />
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setActiveSlide((s) => (s + 1) % heroSlides.length)}
                    className="w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-brand-red hover:border-brand-red transition-all duration-300 backdrop-blur-sm"
                    aria-label="Próximo banner"
                  >
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>


            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 w-full h-[2px] bg-white/10 z-30">
          <div
            key={activeSlide}
            className={`h-full bg-brand-red origin-left ${paused ? "hero-progress-bar--paused" : "hero-progress-bar"}`}
          />
        </div>

      </section>

      {/* ─── VANTAGENS ───────────────────────────────────────── */}
      <section id="vantagens" className="gsap-stack relative bg-[#F8FAFC] flex flex-col justify-center py-8 px-4 lg:px-8 overflow-hidden">

        {/* Decorative blobs */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
          <div className="deco-blob top-0 -left-[5%] w-[40%] h-[40%] bg-brand-blue/5 blur-3xl" />
          <div className="deco-blob bottom-0 -right-[5%] w-[40%] h-[40%] bg-brand-red/5 blur-3xl" />
        </div>

        <div className="container mx-auto max-w-[1300px]">
          <div className="flex flex-col gap-5 lg:gap-6">

            {/* ── LINHA SUPERIOR: Cabeçalho + Estatísticas ── */}
            <div className="flex flex-col lg:flex-row gap-5 lg:gap-6 items-center">

              {/* Cabeçalho */}
              <div className="lg:w-5/12 w-full text-center lg:text-left">
                <div className="mb-2">
                  <span className="inline-block py-1 px-3 rounded-full bg-brand-red/10 text-brand-red font-bold text-[10px] tracking-widest uppercase">
                    Diferenciais Toraflex
                  </span>
                </div>
                <h2 className="font-heading font-black text-3xl md:text-4xl text-brand-blue mb-3 leading-tight tracking-tight">
                  Tradição e excelência em cada detalhe
                </h2>
                <p className="text-slate-500 text-sm md:text-base">
                  Veja por que centenas de clientes escolhem a Toraflex como referência em qualidade.
                </p>
              </div>

              {/* Estatísticas */}
              <div className="lg:w-7/12 w-full">
                <div className="bg-white rounded-[1.5rem] shadow-sm border border-slate-100 p-4 md:p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-200/80 h-full">

                    <div className="text-center py-3 sm:py-0 sm:px-2 lg:px-4 flex flex-col items-center justify-center h-full">
                      <div className="font-heading text-[3rem] lg:text-[3.5rem] text-brand-red font-black tracking-tighter leading-none mb-1">40+</div>
                      <h3 className="text-brand-blue font-bold text-sm md:text-base leading-tight mb-1">Anos de Mercado</h3>
                      <p className="text-slate-500 text-[11px] leading-relaxed max-w-[140px] mx-auto">Tradição e excelência desde 1983</p>
                    </div>

                    <div className="text-center py-3 sm:py-0 sm:px-2 lg:px-4 flex flex-col items-center justify-center h-full">
                      <div className="font-heading text-[3rem] lg:text-[3.5rem] text-brand-red font-black tracking-tighter leading-none mb-1">10M</div>
                      <h3 className="text-brand-blue font-bold text-sm md:text-base leading-tight mb-1">Colchões Produzidos</h3>
                      <p className="text-slate-500 text-[11px] leading-relaxed max-w-[140px] mx-auto">Qualidade em milhões de lares</p>
                    </div>

                    <div className="text-center py-3 sm:py-0 sm:px-2 lg:px-4 flex flex-col items-center justify-center h-full">
                      <div className="font-heading text-[3rem] lg:text-[3.5rem] text-brand-red font-black tracking-tighter leading-none mb-1">20k<span className="text-2xl lg:text-3xl ml-1">m²</span></div>
                      <h3 className="text-brand-blue font-bold text-sm md:text-base leading-tight mb-1">Área Fabril</h3>
                      <p className="text-slate-500 text-[11px] leading-relaxed max-w-[140px] mx-auto">Estrutura moderna em Betim, MG</p>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            {/* ── LINHA INFERIOR: Card Fábrica Nº 1 + 4 Diferenciais ── */}
            <div className="flex flex-col lg:flex-row gap-5 lg:gap-6 items-stretch">

              {/* Card azul principal */}
              <div className="lg:w-5/12 w-full">
                <div className="bg-brand-blue rounded-[1.5rem] p-6 md:p-8 relative overflow-hidden flex flex-col justify-center group shadow-md w-full h-full">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-red opacity-10 rounded-full blur-2xl translate-y-1/4 -translate-x-1/4" />
                  <div className="absolute -top-4 -right-4 text-white/5 text-9xl transform rotate-12">
                    <Crown className="w-20 h-20" />
                  </div>
                  <div className="relative z-10">
                    <div className="w-10 h-10 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center mb-4 border border-white/20">
                      <Medal className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-heading font-black text-2xl md:text-3xl text-white leading-[1.1] mb-3">
                      A Fábrica N<sup>º</sup>&nbsp;1{" "}
                      <span className="text-blue-200 text-lg md:text-xl font-bold block mt-1">de colchões em Minas Gerais.</span>
                    </h3>
                    <p className="text-blue-50 text-sm leading-relaxed font-light">
                      Oferecemos os <strong className="text-white font-semibold">melhores produtos</strong> do mercado com alta qualidade, conforto e um compromisso com o seu bem-estar.
                    </p>
                  </div>
                </div>
              </div>

              {/* 4 Diferenciais */}
              <div className="lg:w-7/12 w-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 h-full">

                  {[
                    {
                      title: "Mais durabilidade",
                      desc: "Produtos fabricados com a mais alta tecnologia do mercado, resultando em pura resistência.",
                      svg: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      ),
                    },
                    {
                      title: "Qualidade garantida",
                      desc: "Priorizamos qualidade excepcional, buscando sempre oferecer o melhor aos clientes.",
                      svg: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                        </svg>
                      ),
                    },
                    {
                      title: "Excelente aptidão",
                      desc: "Tecidos e costuras planejados para garantir noites de descanso completamente satisfatórias.",
                      svg: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      ),
                    },
                    {
                      title: "Conforto absoluto",
                      desc: "Máximo de conforto para noites revigorantes e dias sempre cheios de energia.",
                      svg: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      ),
                    },
                  ].map((b) => (
                    <div
                      key={b.title}
                      className="bg-white rounded-[1.5rem] p-4 shadow-sm border border-slate-100 hover:shadow-lg hover:border-brand-blue/30 transition-all duration-300 flex flex-col justify-start group h-full"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="text-brand-blue transition-transform duration-300 group-hover:scale-110 flex-shrink-0">
                          {b.svg}
                        </div>
                        <h4 className="font-heading font-bold text-base text-brand-blue leading-tight">{b.title}</h4>
                      </div>
                      <p className="text-slate-500 text-xs leading-relaxed">{b.desc}</p>
                    </div>
                  ))}

                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ─── PRODUTOS ────────────────────────────────────────── */}
      <section id="produtos" className="gsap-stack py-10 lg:py-14 bg-white relative overflow-hidden flex flex-col justify-center">
        {/* Background decorations */}
        <div className="absolute top-0 w-full h-[500px] bg-gradient-to-b from-slate-50 to-white -z-10" />
        <div className="absolute -right-64 top-20 w-[500px] h-[500px] bg-brand-blue/5 rounded-full blur-3xl -z-10" />

        <div className="container mx-auto px-6 max-w-[1300px]">
          <div className="gsap-section-header flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10 text-center sm:text-left">
            <div>
              <span className="inline-block py-1 px-3 rounded-full bg-brand-red/10 text-brand-red font-bold text-[10px] tracking-widest uppercase mb-3">
                LINHA EXCLUSIVA
              </span>
              <h2 className="font-heading font-black text-3xl md:text-4xl text-brand-blue mb-3 leading-tight tracking-tight">
                Conheça nossos produtos
              </h2>
              <p className="text-base text-slate-500 font-medium">
                Modelos testados e aprovados pelo consumidor brasileiro.
              </p>
            </div>
            <div className="relative shrink-0">
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-16 bg-brand-blue/20 blur-2xl rounded-full -z-10" />
              <Link
                to="/produtos"
                className="group/mainbtn inline-flex items-center justify-center gap-3 bg-brand-blue border-2 border-brand-blue text-white font-bold px-8 py-4 rounded-2xl transition-all duration-300 hover:bg-white hover:text-brand-blue shadow-lg hover:shadow-2xl hover:-translate-y-1"
              >
                <span className="flex items-center gap-3 text-sm tracking-wide">
                  Conhecer todos os produtos
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/mainbtn:translate-x-1" />
                </span>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {products.map((p) => (
              <Link
                key={p.title}
                to={`/produtos?filter=${p.filter}`}
                className="gsap-product-card group bg-white rounded-[2rem] p-2 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 hover:shadow-[0_20px_40px_-12px_rgba(30,58,138,0.15)] hover:border-brand-blue/20 transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-2 cursor-pointer flex flex-col h-full no-underline [will-change:transform]"
              >
                <div className="relative h-52 mb-4 bg-slate-50 [clip-path:inset(0_round_1.5rem)]">
                  <img
                    src={p.img}
                    alt={p.title}
                    className={`object-cover w-full h-full transition-transform duration-700 ${"imgZoom" in p ? "scale-125 group-hover:scale-150" : "group-hover:scale-110"}`}
                    loading="lazy"
                    decoding="async"
                    width={600}
                    height={400}
                  />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-brand-blue text-[10px] font-bold px-3 py-1.5 rounded-full z-20 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    {p.badge}
                  </div>
                </div>
                <div className="flex flex-col flex-1 px-4 pb-4">
                  <h3 className="font-heading font-bold text-xl text-brand-blue mb-2 uppercase tracking-tight">
                    {p.title}
                    {(p as any).titleSub && (
                      <span className="text-sm font-normal normal-case tracking-normal text-slate-400 ml-1">{(p as any).titleSub}</span>
                    )}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-6 flex-1">{p.desc}</p>
                  <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-4 group/btn">
                    <span className="text-brand-blue font-semibold text-sm group-hover/btn:text-brand-red transition-colors">Ver modelos</span>
                    <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-brand-blue group-hover/btn:bg-brand-red group-hover/btn:text-white transition-all duration-300 shadow-sm">
                      <ArrowRight className="w-4 h-4 -rotate-45 group-hover/btn:rotate-0 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

        </div>
      </section>

      {/* ─── AUTORIDADE / HISTÓRIA ───────────────────────────── */}
      <section id="autoridade" className="gsap-stack py-12 lg:py-16 bg-brand-blue relative overflow-hidden flex flex-col justify-center">

        {/* Decorative blobs */}
        <div className="deco-blob top-0 right-0 w-[400px] h-[400px] bg-white/5 blur-3xl" />
        <div className="deco-blob bottom-0 -left-24 w-[300px] h-[300px] bg-brand-red/10 blur-3xl" />

        <div className="container mx-auto px-4 lg:px-6 max-w-[1300px] relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

            {/* BLOCO 1: Título + Texto */}
            <Link to="/historia" className="lg:col-span-7 bg-white rounded-[1.5rem] lg:rounded-[2rem] p-6 lg:p-8 shadow-xl border border-slate-100 flex flex-col justify-between group hover:shadow-2xl transition-all duration-500 cursor-pointer">
              <div>
                <div className="mb-5">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-3">
                    <Clock className="w-4 h-4 text-brand-blue" /> Tradição desde 1983
                  </div>
                  <h2 className="font-heading font-black text-4xl lg:text-[3rem] text-brand-blue leading-none tracking-tight">
                    Nossa <span className="text-brand-red">história</span>
                  </h2>
                </div>
                <div className="space-y-4 text-slate-600 text-sm md:text-[15px] leading-relaxed font-light">
                  <p>
                    Nascemos em 1983 como fabricantes de móveis de madeira maciça e estofados, conhecida como Tora Móveis. Em 1987, investimos em espuma de poliuretano para melhorar nossa confecção de estofados e foi aí que os colchões entraram em nossa linha de produção.
                  </p>
                  <p>
                    Hoje, possuímos mais de <strong className="font-semibold text-brand-blue">2.000 clientes parceiros Toraflex</strong>, mais de <strong className="font-semibold text-brand-blue">10 milhões de colchões</strong> produzidos em nossa fábrica localizada em Betim, MG, com mais de 20 mil m².
                  </p>
                </div>
              </div>
              <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
                <span className="text-brand-blue font-bold text-sm">Conhecer nossa trajetória</span>
                <div className="w-10 h-10 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center text-brand-blue group-hover:bg-brand-red group-hover:text-white group-hover:border-brand-red transition-all duration-300 shadow-sm hover:scale-110">
                  <ArrowRight className="w-3 h-3 -rotate-45 group-hover:rotate-0 transition-transform duration-300" />
                </div>
              </div>
            </Link>

            {/* BLOCO 2: Imagem + Quote */}
            <div className="lg:col-span-5 relative rounded-[1.5rem] lg:rounded-[2rem] overflow-hidden min-h-[250px] shadow-xl group h-full">
              <img
                src={fabricaImg}
                alt="Fábrica Toraflex"
                className="absolute inset-0 object-cover w-full h-full group-hover:scale-110 transition-transform duration-[2s] ease-out"
                loading="lazy"
                decoding="async"
                width={800}
                height={600}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/90 via-brand-blue/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 md:p-5 text-white transform translate-y-1 group-hover:translate-y-0 transition-transform duration-500">
                <Quote className="w-5 h-5 text-brand-red mb-2 opacity-80" />
                <p className="italic text-sm md:text-base leading-snug font-medium">
                  Cada detalhe presente em nossa rotina, vira qualidade nos produtos que você adquire.
                </p>
              </div>
            </div>

            {/* BLOCO 3: Missão */}
            <div className="lg:col-span-4 bg-white/10 backdrop-blur-md rounded-[1.5rem] lg:rounded-[2rem] p-6 text-white border border-white/10 shadow-lg hover:-translate-y-1 transition-transform duration-300">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center mb-4 border border-white/20">
                <Target className="w-5 h-5" />
              </div>
              <h3 className="font-heading font-bold text-xl mb-2">Missão</h3>
              <p className="text-blue-100 text-xs md:text-sm leading-relaxed font-light">
                Surpreender as pessoas com produtos de qualidade que gerem conforto, qualidade de vida e felicidade.
              </p>
            </div>

            {/* BLOCO 4: Visão */}
            <div className="lg:col-span-4 bg-brand-red rounded-[1.5rem] lg:rounded-[2rem] p-6 text-white shadow-lg border border-red-500 hover:-translate-y-1 transition-transform duration-300 relative overflow-hidden">
              <div className="absolute -right-6 -top-6 w-24 h-24 bg-white/10 rounded-full blur-xl" />
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center mb-4 border border-white/20 relative z-10">
                <Eye className="w-5 h-5" />
              </div>
              <h3 className="font-heading font-bold text-xl mb-2 relative z-10">Visão</h3>
              <p className="text-red-100 text-xs md:text-sm leading-relaxed font-light relative z-10">
                Em até dez anos, ser líder de mercado na venda de colchões da região Sudeste do Brasil.
              </p>
            </div>

            {/* BLOCO 5: Valores */}
            <div className="lg:col-span-4 bg-white rounded-[1.5rem] lg:rounded-[2rem] p-6 text-brand-blue shadow-lg hover:-translate-y-1 transition-transform duration-300">
              <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center mb-4 border border-slate-100">
                <Gem className="w-5 h-5" />
              </div>
              <h3 className="font-heading font-bold text-xl mb-3">Valores</h3>
              <ul className="space-y-2 text-slate-500 text-xs">
                {["Trabalho dedicado e produtivo", "Solidez e Qualidade contínua", "Inovação, Ética e Transparência"].map((v) => (
                  <li key={v} className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-brand-blue/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-2 h-2 text-brand-blue" />
                    </div>
                    <span>{v}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* ─── QUALIDADE ───────────────────────────────────────── */}
      <section id="qualidade" className="gsap-stack py-12 lg:py-16 bg-white relative overflow-hidden flex flex-col justify-center">

        {/* Decorative blobs */}
        <div className="deco-blob top-20 right-0 w-[500px] h-[500px] bg-brand-blue/[0.02] blur-3xl" />
        <div className="deco-blob bottom-0 -left-24 w-[400px] h-[400px] bg-brand-red/[0.02] blur-3xl" />

        <div className="container mx-auto px-6 max-w-[1300px] relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">

            {/* COLUNA ESQUERDA: Imagem */}
            <div className="gsap-qual-img relative order-2 lg:order-1 mt-6 lg:mt-0 group overflow-hidden lg:overflow-visible">
              <div className="hidden lg:block absolute inset-0 bg-brand-blue rounded-[2rem] rotate-[-3deg] scale-[1.02] opacity-5 transition-transform duration-700 group-hover:rotate-[-4deg] group-hover:scale-[1.03]" />
              <div className="relative rounded-[2rem] overflow-hidden shadow-[0_20px_50px_-12px_rgba(30,58,138,0.15)] border-4 border-white">
                <img
                  src={qualidadeImg}
                  alt="Tecnologia do Colchão Toraflex"
                  className="w-full h-[350px] md:h-[420px] object-cover group-hover:scale-[1.05] transition-transform duration-[1500ms] ease-out"
                  loading="lazy"
                  decoding="async"
                  width={800}
                  height={420}
                />
                <div className="absolute inset-0 bg-brand-blue/5 mix-blend-multiply" />
              </div>
              <div className="absolute -bottom-5 -right-2 md:bottom-6 md:-right-6 bg-white/90 backdrop-blur-md px-5 py-3 rounded-2xl shadow-xl border border-white flex items-center gap-3 hover:-translate-y-2 transition-transform duration-500">
                <img src={logoInmetro} alt="Selo INMETRO" className="w-10 h-10 object-contain" width={40} height={40} loading="lazy" />
                <div className="flex flex-col">
                  <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Produto Testado</span>
                  <span className="font-heading font-black text-brand-blue text-xs md:text-sm">Certificado INMETRO</span>
                </div>
              </div>
            </div>

            {/* COLUNA DIREITA: Texto */}
            <div className="gsap-qual-text order-1 lg:order-2 flex flex-col justify-center">
              <div className="mb-3">
                <span className="inline-block py-1 px-3 rounded-full bg-brand-red/10 text-brand-red font-bold text-[10px] tracking-widest uppercase">
                  Durabilidade Comprovada
                </span>
              </div>
              <h2 className="font-heading font-black text-3xl lg:text-4xl text-brand-blue mb-4 leading-[1.1] tracking-tight">
                Colchões feitos para durar.{" "}
                <span className="text-brand-red">Negócios feitos para prosperar.</span>
              </h2>
              <p className="text-slate-600 text-sm md:text-base mb-6 leading-relaxed font-light">
                Reduza as trocas e aumente a satisfação do seu cliente final. Trabalhamos apenas com{" "}
                <strong className="font-semibold text-brand-blue">espumas de alta densidade</strong> e{" "}
                <strong className="font-semibold text-brand-blue">molas de última geração</strong>.
              </p>

              <div className="space-y-3">
                {[
                  {
                    icon: <Medal className="w-5 h-5" />,
                    title: "Qualidade Superior",
                    desc: "Materiais rigorosamente testados em laboratório para garantir uma vida útil estendida e alto desempenho.",
                  },
                  {
                    icon: <ShieldCheck className="w-5 h-5" />,
                    title: "Zero Dor de Cabeça",
                    desc: "Produto bom significa cliente satisfeito, reduzindo drasticamente qualquer devolução ou pedido de troca.",
                  },
                  {
                    icon: <Headset className="w-5 h-5" />,
                    title: "Apoio Total no Pós-Venda",
                    desc: "Nosso suporte não termina na entrega. Ajudamos a sua loja com vendas, marketing e um processo ágil.",
                  },
                ].map((item) => (
                  <div key={item.title} className="group/item flex gap-4 items-start p-3 -ml-3 rounded-xl hover:bg-slate-50 transition-colors duration-300">
                    <div className="flex-shrink-0 w-12 h-12 bg-white rounded-xl flex items-center justify-center text-brand-blue shadow-sm border border-slate-100 group-hover/item:bg-brand-blue group-hover/item:text-white transition-all duration-300 group-hover/item:scale-110">
                      {item.icon}
                    </div>
                    <div className="pt-0.5">
                      <h3 className="font-heading font-bold text-base text-brand-blue mb-0.5">{item.title}</h3>
                      <p className="text-slate-500 text-[13px] leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>



      {/* ─── VÍDEOS ──────────────────────────────────────────── */}
      <section id="na-pratica" className="gsap-stack min-h-screen lg:min-h-0 py-10 lg:py-12 bg-slate-900 relative overflow-hidden flex flex-col justify-center">

        {/* Ambient lights */}
        <div className="deco-blob top-0 left-1/4 w-[500px] h-[500px] bg-brand-blue/20 blur-3xl -translate-y-1/2" />
        <div className="deco-blob bottom-0 right-0 w-[400px] h-[400px] bg-brand-red/10 blur-3xl translate-y-1/4" />

        <div className="container mx-auto px-4 lg:px-6 max-w-[1400px] relative z-10">

          {/* Header split */}
          <div className="gsap-section-header flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4 lg:gap-8 text-center md:text-left">
            <h2 className="font-heading font-black text-3xl md:text-4xl text-white leading-tight tracking-tight md:max-w-lg">
              Conheça a linha completa Toraflex.
            </h2>
            <p className="text-sm md:text-base text-slate-400 font-medium md:max-w-sm lg:max-w-md">
              Cada modelo pensado para atender um perfil de consumidor. Aperte o play e descubra os detalhes.
            </p>
          </div>

          {/* Carousel */}
          <div className="relative group/carousel mt-2">

            {/* Seta esquerda */}
            <button
              type="button"
              aria-label="Vídeos anteriores"
              onClick={() => scrollToVideoPage(Math.max(0, activeVideoPage - 1))}
              className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 md:-translate-x-4 z-20 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-brand-red hover:border-brand-red transition-all duration-300 md:opacity-0 md:group-hover/carousel:opacity-100 shadow-[0_8px_30px_rgb(0,0,0,0.5)] ${activeVideoPage === 0 ? "pointer-events-none !opacity-0" : ""}`}
            >
              <ChevronLeft className="w-3.5 h-3.5 md:w-4 md:h-4" />
            </button>

            {/* Seta direita */}
            <button
              type="button"
              aria-label="Próximos vídeos"
              onClick={() => scrollToVideoPage(Math.min(totalPages - 1, activeVideoPage + 1))}
              className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 md:translate-x-4 z-20 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-brand-red hover:border-brand-red transition-all duration-300 md:opacity-0 md:group-hover/carousel:opacity-100 shadow-[0_8px_30px_rgb(0,0,0,0.5)] ${activeVideoPage >= totalPages - 1 ? "pointer-events-none !opacity-0" : ""}`}
            >
              <ChevronRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
            </button>

            <div
              ref={setVideoRef}
              onScroll={handleVideoScroll}
              className="flex overflow-x-auto gap-4 md:gap-5 pb-6 pt-2 snap-x snap-mandatory px-2 md:px-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] scroll-smooth touch-pan-x"
              style={videoDrag.style}
              {...videoDrag.handlers}
            >
              {vimeoVideos.map((v, i) => (
                <VimeoVideoCard key={`${v.vimeoId}-${i}`} {...v} />
              ))}
            </div>
          </div>

        </div>
      </section>


      {/* ─── DEPOIMENTOS + PARCEIROS ─────────────────────────────── */}
      <section id="depoimentos" className="gsap-stack pt-12 lg:pt-20 pb-4 bg-white flex flex-col justify-start relative overflow-hidden">

        {/* Upper: Depoimentos */}
        <div className="container mx-auto px-6 max-w-[1200px] relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">

            {/* Text (left) */}
            <div className="lg:col-span-5 flex flex-col justify-center text-center lg:text-left">
              <span className="inline-block py-1 px-3 rounded-full bg-brand-red/10 text-brand-red font-bold text-[10px] tracking-widest uppercase mb-4 self-center lg:self-start">
                Depoimentos
              </span>
              <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl text-brand-blue mb-4 leading-tight tracking-tight">
                Quem tem Toraflex na loja, <br className="hidden lg:block" />
                <span className="text-brand-red">não troca.</span>
              </h2>
              <p className="text-slate-500 text-sm md:text-base leading-relaxed">
                Veja o que nossos clientes estão falando sobre a qualidade dos produtos e o suporte que oferecemos no dia a dia.
              </p>
            </div>

            {/* Videos (right) – staggered */}
            <div className="lg:col-span-7 flex justify-center lg:justify-end gap-4 md:gap-6">

              {/* Video 1 – shifted down */}
              <div className="aspect-9-16 relative w-[150px] md:w-[220px] rounded-2xl md:rounded-[2rem] overflow-hidden shadow-2xl group cursor-pointer border-4 border-white transform lg:translate-y-6 hover:-translate-y-1 lg:hover:translate-y-4 transition-all duration-500 bg-slate-800">
                <VimeoVideoCard vimeoId="1166833948" fallbackTitle="Depoimento Ricardo" fluid forceTitle />
              </div>

              {/* Video 2 – shifted up */}
              <div className="aspect-9-16 relative w-[150px] md:w-[220px] rounded-2xl md:rounded-[2rem] overflow-hidden shadow-2xl group cursor-pointer border-4 border-white transform lg:-translate-y-6 hover:-translate-y-2 lg:hover:-translate-y-8 transition-all duration-500 bg-slate-800">
                <VimeoVideoCard vimeoId="1166833921" fallbackTitle="Depoimento Aguiar" fluid forceTitle />
              </div>

            </div>
          </div>
        </div>

        {/* Lower: Parceiros marquee */}
        <div className="mt-12 lg:mt-16 border-t border-slate-100 py-8 relative overflow-hidden">

          {/* Fade edges */}
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

              <div className="text-center mb-6 relative z-20">
                <h3 className="text-brand-blue font-bold text-xs uppercase tracking-[0.15em]">Clientes que confiam na Toraflex</h3>
              </div>

              {(() => {
                const allLogos = [
                  { src: logoComfort, alt: "Comfort Colchões" },
                  { src: logoTopColchoes, alt: "Top Colchões" },
                  { src: logoTemTudo, alt: "Tem Tudo" },
                  { src: logoLbxMoveis, alt: "LBX Móveis" },
                  { src: logoMobigu, alt: "Mobigu" },
                  { src: logoSonhare, alt: "Sonhare Colchões" },
                  { src: logoAguiarFlex, alt: "Aguiar Flex Móveis" },
                  { src: logoCiaSouza, alt: "Loja Cia Souza" },
                  { src: logoColchoesCastro, alt: "Colchões Castro" },
                  { src: logoPauEFilhos, alt: "Pau & Filhos" },
                  { src: logoLvMoveis, alt: "LV Móveis" },
                  { src: logoMgColchoes, alt: "MG Colchões" },
                  { src: logoMaiaMoveis, alt: "Maia Móveis" },
                  { src: logoColchoariaClick, alt: "Colchoaria Click" },
                  { src: logoMoveisOutlet, alt: "Móveis Outlet" },
                  { src: logoShoppingMoveis, alt: "Shopping dos Móveis" },
                ];
                const doubled = [...allLogos, ...allLogos];
                return (
                  <div
                    ref={logosDrag.ref}
                    className="flex overflow-x-auto w-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] touch-pan-x"
                    style={logosDrag.style}
                    {...logosDrag.handlers}
                  >
                    <div className="flex animate-marquee gap-8 md:gap-12 items-center w-max">
                      {doubled.map((logo, i) => (
                        <div key={i} className="flex items-center justify-center shrink-0 h-20 md:h-24 grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-300">
                          <img src={logo.src} alt={logo.alt} className="max-h-20 md:max-h-24 w-auto object-contain" loading="lazy" width={200} height={96} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
        </div>

      </section>


      {/* ─── AVALIAÇÕES + FAQ ────────────────────────────────── */}
      <section className="gsap-stack py-10 lg:py-12 bg-[#F8FAFC] flex flex-col justify-center relative overflow-hidden">

        {/* Background blobs */}
        <div className="deco-blob top-0 left-0 w-[500px] h-[500px] bg-brand-blue/[0.03] blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="deco-blob bottom-0 right-0 w-[400px] h-[400px] bg-brand-red/[0.03] blur-3xl translate-x-1/3 translate-y-1/3" />

        {/* ── Avaliações marquee ── */}
        <div className="mb-10 lg:mb-12">
          <div className="container mx-auto px-6 max-w-[1300px] relative z-10 text-center mb-6">
            <span className="inline-block py-1 px-3 rounded-full bg-brand-red/10 text-brand-red font-bold text-[10px] tracking-widest uppercase mb-2">
              Avaliações do Google
            </span>
            <h2 className="font-heading font-black text-2xl md:text-3xl text-brand-blue leading-tight tracking-tight">
              O que nossos clientes estão falando
            </h2>
          </div>

          <div className="relative flex overflow-hidden w-full pb-2">
            <div className="absolute left-0 top-0 bottom-0 w-12 md:w-32 bg-gradient-to-r from-[#F8FAFC] to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-12 md:w-32 bg-gradient-to-l from-[#F8FAFC] to-transparent z-10 pointer-events-none" />

            {(() => {
              const reviews = [
                { initials: "MR", color: "bg-blue-500", name: "Mário Rizzo", text: "Muito interessante entender como é fabricado um produto de referência em qualidade, líder de mercado com absoluta certeza." },
                { initials: "FT", color: "bg-green-500", name: "Fabio Teixeira", text: "Pra mim o melhor de todos foi a primeira marca que comprei quando sai da casa da minha mãe maravilhoso." },
                { initials: "MS", color: "bg-purple-500", name: "Michel Santos", text: "Ótimo colchão, acho que melhoraram muito na qualidade, vale a pena. Não julguem antes de experimentar na loja." },
                { initials: "AF", color: "bg-pink-500", name: "Aparecida Fernandes", text: "Esse colchão é maravilhoso não deforma amei." },
                { initials: "FC", color: "bg-orange-500", name: "Fabricio Caixeta", text: "Colchões de alta qualidade... Sempre recomendo." },
                { initials: "CV", color: "bg-teal-500", name: "Criar e Viajar", text: "Super indico, colchão de ótima qualidade." },
              ];
              const doubled = [...reviews, ...reviews];
              return (
                <div
                  ref={reviewsDrag.ref}
                  className="flex overflow-x-auto w-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] touch-pan-x"
                  style={reviewsDrag.style}
                  {...reviewsDrag.handlers}
                >
                <div className="flex animate-marquee gap-4 w-max px-4">
                  {doubled.map((r, i) => (
                    <div key={i} className="bg-white border border-slate-100 rounded-[1.25rem] p-5 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col h-[180px] w-[280px] md:w-[320px] shrink-0">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex gap-1">
                          {[...Array(5)].map((_, s) => <Star key={s} className="w-3 h-3 text-amber-500 fill-amber-500" />)}
                        </div>
                        <GoogleIcon className="w-5 h-5 text-slate-300" />
                      </div>
                      <p className="text-slate-600 text-[13px] leading-snug flex-1 font-medium italic">"{r.text}"</p>
                      <div className="flex items-center gap-3 pt-3 border-t border-slate-50 mt-auto">
                        <div className={`w-8 h-8 rounded-full ${r.color} flex items-center justify-center text-white font-bold text-[10px] shrink-0`}>{r.initials}</div>
                        <div className="leading-tight">
                          <p className="font-bold text-[13px] text-brand-blue">{r.name}</p>
                          <p className="text-[9px] text-slate-500 font-semibold uppercase tracking-wider">Avaliação no Google</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* ── FAQ ── */}
        <div id="faq" className="container mx-auto px-6 max-w-3xl relative z-10">
          <div className="text-center mb-6">
            <h2 className="font-heading font-black text-xl md:text-2xl text-brand-blue mb-2">
              Dúvidas Frequentes
            </h2>
            <p className="text-slate-500 text-xs md:text-sm font-medium">
              Tire suas dúvidas antes de falar com nosso consultor.
            </p>
          </div>

          <FaqList faqs={faqs} />

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setWaModalOpen(true)}
              className="inline-flex items-center gap-2 text-brand-blue font-bold text-xs hover:text-brand-red transition-colors border-b-2 border-brand-blue/20 hover:border-brand-red pb-0.5"
            >
              Ainda tem dúvidas? Fale com nosso consultor <ArrowRight className="w-3 h-3 inline" />
            </button>
          </div>
        </div>

      </section>

      {/* ─── TRABALHE CONOSCO / CONTATO ────────────────────── */}
      <section id="trabalhe-conosco" className="gsap-stack py-20 relative flex items-center min-h-[70vh]">
        <img src={bgForm} alt="" className="absolute inset-0 w-full h-full object-cover grayscale" loading="lazy" width={1920} height={1080} />
        <div className="absolute inset-0 bg-slate-900/90 pointer-events-none" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left */}
            <div className="gsap-contato-left text-white text-center lg:text-left">
              <h2 className="font-heading font-black text-3xl md:text-5xl mb-6 leading-tight">
                Entre em contato <br />
                com a <span className="text-brand-red">Toraflex</span>
              </h2>
              <p className="text-slate-300 text-lg md:text-xl mb-8 leading-relaxed">
                Interessado em revender colchões Toraflex? Preencha o formulário com seus dados e entraremos em contato para apresentar nossas condições.
              </p>
            </div>

            {/* Right — Form card */}
            <div className="gsap-contato-form bg-white rounded-2xl shadow-2xl p-6 md:p-8">
              <div className="mb-6">
                <h3 className="text-slate-900 font-heading font-bold text-xl mb-1">
                  Solicitar Contato
                </h3>
                <p className="text-slate-500 text-sm">
                  Preencha para falar com o representante.
                </p>
              </div>
              <LeadForm
                onSubmit={async (data) => {
                  try {
                    const { error } = await supabase.functions.invoke("send-contact-email", {
                      body: { nome: data.nome.trim(), whatsapp: data.whatsapp, cnpj: data.cnpj, cidade: data.cidade.trim(), uf: data.uf },
                    });
                    if (error) throw error;
                    toast.success("Contato enviado para nossos representantes! Escolha o canal de atendimento.");
                  } catch (err) {
                    console.error("Erro ao enviar e-mail:", err);
                    toast.error("Erro ao enviar. Escolha o canal de atendimento.");
                  }
                  setWaModalOpen(true);
                }}
                layout="card"
              />
            </div>
          </div>
        </div>
      </section>

      <FloatingButtons />
      <WhatsAppContactModal open={waModalOpen} onOpenChange={setWaModalOpen} />

      </main>
      <Footer onOpenSidebar={() => setSidebarOpen(true)} />
    </div>
  );
}

